import { useLingui } from '@lingui/react/macro'
import {
  LoadingContainer,
  PageLoadingIndicator,
  PageLoadingSpinner,
} from 'features/knowledgeDocumentViewer/components/knowledgeDocumentToolsStyles'
import {
  type ImageViewerRolodexOverride,
  useImageViewerLogic,
} from 'features/knowledgeDocumentViewer/components/viewer/image/hooks/useImageViewerLogic'
import {
  computePagesToPreload,
  RolodexPagePreloader,
} from 'features/knowledgeDocumentViewer/components/viewer/image/hooks/useRolodexPagePreloader'
import ImageViewerWithOverlay from 'features/knowledgeDocumentViewer/components/viewer/image/ImageViewerWithOverlay'
import type { SearchHighlightData } from 'features/knowledgeDocumentViewer/components/viewer/search/SearchHighlightOverlay'
import type {
  DocumentViewerViewportState,
  PagedViewportState,
} from 'features/knowledgeDocumentViewer/components/viewer/shared/documentViewerViewport'
import { isVirginTransform } from 'features/knowledgeDocumentViewer/components/viewer/shared/hooks/useFitToViewport'
import { useMinimapPreference } from 'features/knowledgeDocumentViewer/components/viewer/shared/hooks/useMinimapPreference'
import { LiveDocumentMinimap } from 'features/knowledgeDocumentViewer/components/viewer/shared/LiveDocumentMinimap'
import { PageImageStatus } from 'features/knowledgeDocumentViewer/components/viewer/shared/PageImageStatus'
import { DocumentViewerRoot } from 'features/knowledgeDocumentViewer/components/viewer/shared/styles'
import { createTransformStore } from 'features/knowledgeDocumentViewer/components/viewer/shared/transformStore'
import {
  type DocumentViewerHandle,
  type FrameTarget,
  type TransformState,
  type ViewerCommonProps,
} from 'features/knowledgeDocumentViewer/components/viewer/shared/types'
import { ZoomControls } from 'features/knowledgeDocumentViewer/components/viewer/shared/ZoomControls'
import { useDocumentTabDataContext } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { useEvalImagePreview } from 'features/knowledgeDocumentViewer/hooks/useEvalImagePreview'
import { usePageImageFailure } from 'features/knowledgeDocumentViewer/hooks/usePageImageFailure'
import { usePageImageOutcome } from 'features/knowledgeDocumentViewer/hooks/usePageImageOutcome'
import { usePageImageRecovery } from 'features/knowledgeDocumentViewer/hooks/usePageImageRecovery'
import { useViewerCantileverPageImageRef } from 'features/knowledgeDocumentViewer/hooks/useViewerCantileverPageImageRef'
import { DocumentViewerPage } from 'features/knowledgeDocumentViewer/types'
import { resolvePageImageFileRef } from 'features/knowledgeDocumentViewer/utils/documentPageImages'
import { resolvePageImageRolodexSettleState } from 'features/knowledgeDocumentViewer/utils/pageImageRolodexSettle'
import { useRolodexSignedURLs } from 'features/rolodex'
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

function resolveRolodexOverride(
  url: string | undefined,
  isLoading: boolean
): ImageViewerRolodexOverride | undefined {
  if (url) return { imageUrl: url, isLoading: false }
  return isLoading ? { imageUrl: undefined, isLoading: true } : undefined
}

export type PagedDocumentViewerProps = ViewerCommonProps & {
  documentId: string
  isActiveTab: boolean
  pageNumber: number
  pdfUrl: string
  scale: number
  totalPages: number
  showMultiplePages: boolean
  searchHighlights?: SearchHighlightData
  frameTarget?: FrameTarget
  onFrameApplied?: () => void
  initialViewport?: PagedViewportState | null
  onViewportChange?: (viewport: DocumentViewerViewportState) => void
}

export const PagedDocumentViewer = forwardRef<
  DocumentViewerHandle,
  PagedDocumentViewerProps
>(function PagedDocumentViewer(
  {
    documentId,
    pageGraphs,
    graphPageNumber,
    onOpenOffPageConnector,
    isActiveTab,
    pageNumber,
    pdfUrl,
    scale,
    totalPages,
    showMultiplePages,
    searchHighlights,
    frameTarget,
    onFrameApplied,
    initialViewport = null,
    onViewportChange,
    highlightBoxIds,
    onBoxClick,
    onBoxAuxClick,
    onBoxContextMenu,
    onBoxHover,
    onMultipleBoxSelect,
    onAliasClick,
    onDocReferenceClick,
    onDocReferenceHover,
    actionMode,
    showTooltips = false,
  },
  ref
) {
  const { t } = useLingui()
  const { isEvalSessionLocked, photoMode, showPhotoCounts, togglePhotoCounts } =
    useKnowledgeDocumentUXContext()
  const viewerRef = useRef<DocumentViewerHandle>(null)
  const viewerRootRef = useRef<HTMLDivElement | null>(null)
  const transformWrapperRef = useRef<HTMLElement | null>(null)
  const onViewportChangeRef = useRef(onViewportChange)
  onViewportChangeRef.current = onViewportChange
  // Ignore TransformWrapper init until a restored transform lands.
  const allowViewportPersistRef = useRef(!initialViewport)
  const initialViewportRef = useRef(initialViewport)
  const [isAtMinZoom, setIsAtMinZoom] = useState(false)
  const [isAtMaxZoom, setIsAtMaxZoom] = useState(false)
  const [zoomStateKnown, setZoomStateKnown] = useState(false)
  const [transformStore] = useState(() =>
    createTransformStore(
      initialViewport
        ? {
            scale: initialViewport.scale,
            positionX: initialViewport.positionX,
            positionY: initialViewport.positionY,
          }
        : { scale: 1, positionX: 0, positionY: 0 }
    )
  )
  const [imageNaturalDimensions, setImageNaturalDimensions] = useState({
    w: 0,
    h: 0,
  })
  const [wrapperDimensions, setWrapperDimensions] = useState({ w: 0, h: 0 })
  const currentScaleRef = useRef(1)
  const isResettingZoomRef = useRef(false)
  const latestTransformStateRef = useRef<TransformState>(transformStore.get())
  const transformFrameRef = useRef<number | null>(null)

  const leftPageRolodexQuery = useViewerCantileverPageImageRef(
    pageNumber,
    'hiRes'
  )
  const {
    url: leftPage6400UrlPrimary,
    isLoading: isLeftPage6400LoadingPrimary,
    forceRefresh: refreshLeftPrimary,
  } = useRolodexSignedURLs(leftPageRolodexQuery)

  const thumbnailQuery = useViewerCantileverPageImageRef(
    pageNumber,
    'thumbnail'
  )
  const { url: thumbnailUrl, forceRefresh: refreshThumbnail } =
    useRolodexSignedURLs(isEvalSessionLocked ? thumbnailQuery : undefined)
  const previewUrl = useEvalImagePreview(
    isEvalSessionLocked,
    leftPage6400UrlPrimary,
    thumbnailUrl
  )

  const hasSecondPage =
    showMultiplePages && totalPages > 1 && pageNumber < totalPages

  const rightPageRolodexQuery = useViewerCantileverPageImageRef(
    pageNumber + 1,
    'hiRes'
  )
  const {
    url: rightPage6400UrlPrimary,
    isLoading: isRightPage6400LoadingPrimary,
    forceRefresh: refreshRightPrimary,
  } = useRolodexSignedURLs(hasSecondPage ? rightPageRolodexQuery : undefined)

  // Legacy ingests have no UUID5-derived page hosts — their images live on
  // `{systemDocumentEntryId}_pg_{n}` hosts. Queried only after the derived
  // host comes back empty.
  const { systemDocumentEntryId, isUuid5PageImageLookupUnavailable } =
    useDocumentTabDataContext()
  const legacyLeftQuery = useMemo(
    () =>
      systemDocumentEntryId
        ? resolvePageImageFileRef(
            `${systemDocumentEntryId}_pg_${pageNumber}`,
            'hiRes'
          )
        : undefined,
    [systemDocumentEntryId, pageNumber]
  )
  const leftPrimarySettle = resolvePageImageRolodexSettleState({
    hasQuery: leftPageRolodexQuery !== undefined,
    lookupUnavailable: isUuid5PageImageLookupUnavailable,
    url: leftPage6400UrlPrimary,
    isLoading: isLeftPage6400LoadingPrimary,
  })
  const leftPrimaryMissed = leftPrimarySettle.isFailed
  const {
    url: leftPageLegacyUrl,
    isLoading: isLeftPageLegacyLoading,
    forceRefresh: refreshLeftLegacy,
  } = useRolodexSignedURLs(leftPrimaryMissed ? legacyLeftQuery : undefined)

  const legacyRightQuery = useMemo(
    () =>
      systemDocumentEntryId
        ? resolvePageImageFileRef(
            `${systemDocumentEntryId}_pg_${pageNumber + 1}`,
            'hiRes'
          )
        : undefined,
    [systemDocumentEntryId, pageNumber]
  )
  const rightPrimarySettle = resolvePageImageRolodexSettleState({
    hasQuery: rightPageRolodexQuery !== undefined,
    lookupUnavailable: isUuid5PageImageLookupUnavailable,
    url: rightPage6400UrlPrimary,
    isLoading: isRightPage6400LoadingPrimary,
  })
  const rightPrimaryMissed = hasSecondPage && rightPrimarySettle.isFailed
  const {
    url: rightPageLegacyUrl,
    isLoading: isRightPageLegacyLoading,
    forceRefresh: refreshRightLegacy,
  } = useRolodexSignedURLs(rightPrimaryMissed ? legacyRightQuery : undefined)

  const leftPage6400Url = previewUrl ?? leftPageLegacyUrl
  const isLeftPage6400Loading =
    isLeftPage6400LoadingPrimary ||
    isLeftPageLegacyLoading ||
    leftPrimarySettle.isPending
  const rightPage6400Url = rightPage6400UrlPrimary ?? rightPageLegacyUrl
  const isRightPage6400Loading =
    isRightPage6400LoadingPrimary ||
    isRightPageLegacyLoading ||
    rightPrimarySettle.isPending

  const leftOutcome = usePageImageOutcome({
    pageNumber,
    surface: 'paged',
    isCurrentPage: isActiveTab,
  })
  const leftImageKey = `${documentId}:${pageNumber}`
  const refreshLeftSources = useCallback(async () => {
    await Promise.all([
      refreshLeftPrimary(),
      refreshLeftLegacy(),
      isEvalSessionLocked ? refreshThumbnail() : Promise.resolve(),
    ])
  }, [
    isEvalSessionLocked,
    refreshLeftLegacy,
    refreshLeftPrimary,
    refreshThumbnail,
  ])
  const refreshRightSources = useCallback(async () => {
    await Promise.all([refreshRightPrimary(), refreshRightLegacy()])
  }, [refreshRightLegacy, refreshRightPrimary])
  const leftRecovery = usePageImageRecovery({
    resetKey: leftImageKey,
    url: leftPage6400Url,
    forceRefresh: refreshLeftSources,
    onRetry: leftOutcome.markRetried,
  })
  const rightRecovery = usePageImageRecovery({
    resetKey: `${documentId}:${pageNumber + 1}`,
    url: rightPage6400Url,
    forceRefresh: refreshRightSources,
  })

  const rolodexOverrideLeft = useMemo(
    () => resolveRolodexOverride(leftRecovery.url, isLeftPage6400Loading),
    [leftRecovery.url, isLeftPage6400Loading]
  )

  const rolodexOverrideRight = useMemo(
    () => resolveRolodexOverride(rightRecovery.url, isRightPage6400Loading),
    [rightRecovery.url, isRightPage6400Loading]
  )
  const leftSettle = resolvePageImageRolodexSettleState({
    hasQuery: leftPageRolodexQuery !== undefined,
    lookupUnavailable: isUuid5PageImageLookupUnavailable,
    url: leftRecovery.url,
    isLoading: isLeftPage6400Loading,
  })

  const pagesToPreload = useMemo(
    () =>
      rolodexOverrideLeft && !isEvalSessionLocked
        ? computePagesToPreload(pageNumber, totalPages, showMultiplePages)
        : [],
    [
      rolodexOverrideLeft,
      pageNumber,
      totalPages,
      showMultiplePages,
      isEvalSessionLocked,
    ]
  )

  const pdfViewerEnabled = isActiveTab

  const {
    imageUrl: imageUrlLeftPage,
    boxes: boxesLeftPage,
    isLoading: isLoadingLeftPage,
    pdfError: leftPdfError,
    pdfStale: leftPdfStale,
    reloadPdf: reloadLeftPdf,
  } = useImageViewerLogic(
    pdfUrl || undefined,
    pageNumber,
    scale,
    DocumentViewerPage.LEFT_PAGE,
    totalPages,
    pdfViewerEnabled,
    rolodexOverrideLeft
  )

  const {
    imageUrl: imageUrlRightPage,
    boxes: boxesRightPage,
    isLoading: isLoadingRightPage,
  } = useImageViewerLogic(
    pdfUrl || undefined,
    pageNumber + 1,
    scale,
    DocumentViewerPage.RIGHT_PAGE,
    totalPages,
    pdfViewerEnabled,
    rolodexOverrideRight
  )

  const leftFailure = usePageImageFailure({
    imageKey: leftImageKey,
    rolodexUrl: leftPage6400Url,
    recovery: leftRecovery,
    outcome: leftOutcome,
    pdf: { error: leftPdfError, reload: reloadLeftPdf },
    pdfAttempted: !rolodexOverrideLeft && pdfViewerEnabled && !!pdfUrl,
    hasDisplayUrl: !!imageUrlLeftPage,
    isLoading: isLoadingLeftPage,
    rolodexMissed: leftSettle.isFailed,
    lookupUnavailable: isUuid5PageImageLookupUnavailable,
    isCurrentPage: isActiveTab,
  })

  const handleImageLoaded = useCallback(
    (index: number, src: string) => {
      if (index !== 0) {
        rightRecovery.onLoad(src)
        return
      }
      leftRecovery.onLoad(src)
      if (src === leftPage6400Url) {
        leftOutcome.reportDisplayed(previewUrl ? 'uuid5' : 'legacy')
      } else if (!leftPdfStale) {
        leftOutcome.reportDisplayed('pdf')
      }
    },
    [
      leftOutcome,
      leftPage6400Url,
      leftPdfStale,
      leftRecovery,
      previewUrl,
      rightRecovery,
    ]
  )

  const handleImageError = useCallback(
    (index: number, src: string) => {
      if (index !== 0) {
        rightRecovery.onError(src)
        return
      }
      leftFailure.onImageError(src)
    },
    [leftFailure, rightRecovery]
  )

  const combinedBoxes = useMemo(
    () =>
      showMultiplePages && totalPages > 1 && hasSecondPage
        ? [...boxesLeftPage, ...boxesRightPage]
        : boxesLeftPage,
    [
      showMultiplePages,
      totalPages,
      hasSecondPage,
      boxesLeftPage,
      boxesRightPage,
    ]
  )

  const combinedImageUrls = useMemo(() => {
    if (
      showMultiplePages &&
      totalPages > 1 &&
      hasSecondPage &&
      imageUrlRightPage &&
      imageUrlLeftPage
    ) {
      return [imageUrlLeftPage, imageUrlRightPage]
    }

    return imageUrlLeftPage ? [imageUrlLeftPage] : undefined
  }, [
    hasSecondPage,
    imageUrlLeftPage,
    imageUrlRightPage,
    showMultiplePages,
    totalPages,
  ])

  const displayImageUrls = combinedImageUrls
  const minimapImageUrls = displayImageUrls ?? []
  const isMinimapZoomed =
    zoomStateKnown && !isAtMinZoom && !isResettingZoomRef.current
  const isMinimapContentReady =
    imageNaturalDimensions.w > 0 &&
    wrapperDimensions.w > 0 &&
    minimapImageUrls.length > 0
  const { showMinimap, isMinimapEnabled, toggleMinimap } = useMinimapPreference(
    {
      isZoomed: isMinimapZoomed,
      isContentReady: isMinimapContentReady,
    }
  )

  useEffect(() => {
    currentScaleRef.current = 1
    setZoomStateKnown(false)
  }, [documentId])

  const handleZoomStateChange = useCallback(
    (state: { isAtMinZoom: boolean; isAtMaxZoom: boolean }) => {
      setIsAtMinZoom(state.isAtMinZoom)
      setIsAtMaxZoom(state.isAtMaxZoom)
      setZoomStateKnown(true)

      if (state.isAtMinZoom) {
        isResettingZoomRef.current = false
      }
    },
    []
  )

  const updateWrapperDimensions = useCallback(
    (next: { w: number; h: number }) => {
      setWrapperDimensions((prev) => {
        if (prev.w === next.w && prev.h === next.h) return prev
        return next
      })
    },
    []
  )

  const updateImageNaturalDimensions = useCallback(
    (next: { w: number; h: number }) => {
      setImageNaturalDimensions((prev) => {
        if (prev.w === next.w && prev.h === next.h) return prev
        return next
      })
    },
    []
  )

  const persistViewport = useCallback((state: TransformState) => {
    const restored = initialViewportRef.current
    if (!allowViewportPersistRef.current && restored) {
      const matchesRestored =
        Math.abs(state.scale - restored.scale) < 0.02 &&
        Math.abs(state.positionX - restored.positionX) < 2 &&
        Math.abs(state.positionY - restored.positionY) < 2
      if (!matchesRestored) return
      allowViewportPersistRef.current = true
    }
    onViewportChangeRef.current?.({
      kind: 'paged',
      scale: state.scale,
      positionX: state.positionX,
      positionY: state.positionY,
    })
  }, [])

  const handleTransformChange = useCallback(
    (state: TransformState) => {
      currentScaleRef.current = state.scale
      latestTransformStateRef.current = state
      persistViewport(state)

      if (transformFrameRef.current !== null) return

      transformFrameRef.current = requestAnimationFrame(() => {
        transformFrameRef.current = null
        transformStore.set(latestTransformStateRef.current)
      })
    },
    [persistViewport, transformStore]
  )

  useEffect(() => {
    return () => {
      if (!allowViewportPersistRef.current) return
      const latest = latestTransformStateRef.current
      // Do not persist the virgin initial transform.
      if (isVirginTransform(latest)) return
      onViewportChangeRef.current?.({
        kind: 'paged',
        scale: latest.scale,
        positionX: latest.positionX,
        positionY: latest.positionY,
      })
    }
  }, [])

  const handleImageDimensionsChange = useCallback(
    (dimensions: { w: number; h: number }) => {
      updateImageNaturalDimensions(dimensions)
    },
    [updateImageNaturalDimensions]
  )

  const syncViewerMeasurements = useCallback(() => {
    const viewportInfo = viewerRef.current?.getViewportInfo()
    if (viewportInfo) {
      updateWrapperDimensions({
        w: viewportInfo.wrapperWidth,
        h: viewportInfo.wrapperHeight,
      })
    }

    const naturalDimensions = viewerRef.current?.getImageNaturalDimensions()
    if (
      naturalDimensions &&
      naturalDimensions.w > 0 &&
      naturalDimensions.h > 0
    ) {
      updateImageNaturalDimensions(naturalDimensions)
    }
  }, [updateImageNaturalDimensions, updateWrapperDimensions])

  useEffect(() => {
    const element = viewerRootRef.current
    if (!element) return

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (!entry) return

      updateWrapperDimensions({
        w: entry.contentRect.width,
        h: entry.contentRect.height,
      })
    })

    observer.observe(element)
    return () => observer.disconnect()
  }, [updateWrapperDimensions])

  useEffect(() => {
    return () => {
      if (transformFrameRef.current !== null) {
        cancelAnimationFrame(transformFrameRef.current)
      }
    }
  }, [])

  useEffect(() => {
    const frameId = requestAnimationFrame(() => {
      syncViewerMeasurements()
    })

    return () => cancelAnimationFrame(frameId)
  }, [
    combinedImageUrls,
    pageNumber,
    pdfUrl,
    scale,
    showMultiplePages,
    syncViewerMeasurements,
  ])

  const viewerFrameTarget = useMemo<
    | {
        bbox: FrameTarget['bbox']
        pageIndex: 0 | 1
        framing: FrameTarget['framing']
      }
    | undefined
  >(() => {
    if (!frameTarget) return undefined
    if (frameTarget.pageNumber === pageNumber) {
      return {
        bbox: frameTarget.bbox,
        pageIndex: 0,
        framing: frameTarget.framing,
      }
    }
    if (hasSecondPage && frameTarget.pageNumber === pageNumber + 1) {
      return {
        bbox: frameTarget.bbox,
        pageIndex: 1,
        framing: frameTarget.framing,
      }
    }
    return undefined
  }, [frameTarget, pageNumber, hasSecondPage])

  const handleZoomOut = useCallback(() => {
    viewerRef.current?.zoomOut()
  }, [])

  const handleZoomIn = useCallback(() => {
    viewerRef.current?.zoomIn()
  }, [])

  const handleResetZoom = useCallback(() => {
    isResettingZoomRef.current = true
    viewerRef.current?.resetZoom()
  }, [])

  const handleMinimapNavigate = useCallback(
    (positionX: number, positionY: number, animated: boolean) => {
      viewerRef.current?.setTransform(
        positionX,
        positionY,
        latestTransformStateRef.current.scale,
        animated ? 200 : 0
      )
    },
    []
  )

  useImperativeHandle(
    ref,
    () => ({
      zoomToBox: (id: string, paddingPx?: number) =>
        viewerRef.current?.zoomToBox(id, paddingPx),
      panToRegion: (bbox) => viewerRef.current?.panToRegion?.(bbox),
      setHighlightedBoxIds: (boxIds: string[]) =>
        viewerRef.current?.setHighlightedBoxIds(boxIds),
      resetZoom: handleResetZoom,
      zoomIn: handleZoomIn,
      zoomOut: handleZoomOut,
      setTransform: (posX, posY, nextScale, animTime = 0) =>
        viewerRef.current?.setTransform(posX, posY, nextScale, animTime),
      getViewportInfo: () => viewerRef.current?.getViewportInfo() ?? null,
      getImageNaturalDimensions: () =>
        viewerRef.current?.getImageNaturalDimensions() ?? { w: 0, h: 0 },
    }),
    [handleResetZoom, handleZoomIn, handleZoomOut]
  )

  if (displayImageUrls && displayImageUrls.length > 0) {
    return (
      <DocumentViewerRoot ref={viewerRootRef}>
        <ImageViewerWithOverlay
          ref={viewerRef}
          isActiveTab={isActiveTab}
          pageGraphs={pageGraphs}
          graphPageNumber={graphPageNumber}
          onOpenOffPageConnector={onOpenOffPageConnector}
          transformWrapperRef={transformWrapperRef}
          imageUrls={displayImageUrls}
          boxes={combinedBoxes}
          searchHighlights={searchHighlights}
          frameTarget={viewerFrameTarget}
          onFrameApplied={onFrameApplied}
          restoredTransform={initialViewport}
          documentId={documentId}
          progressiveImageKey={
            isEvalSessionLocked ? `${documentId}:${pageNumber}` : undefined
          }
          highlightBoxIds={highlightBoxIds}
          onBoxClick={onBoxClick}
          onBoxAuxClick={onBoxAuxClick}
          onBoxContextMenu={onBoxContextMenu}
          onBoxHover={onBoxHover}
          onMultipleBoxSelect={onMultipleBoxSelect}
          onAliasClick={onAliasClick}
          onDocReferenceClick={onDocReferenceClick}
          onDocReferenceHover={onDocReferenceHover}
          actionMode={actionMode}
          showTooltips={showTooltips}
          onTransformChange={handleTransformChange}
          onImageDimensionsChange={handleImageDimensionsChange}
          onZoomStateChange={handleZoomStateChange}
          onImageLoaded={handleImageLoaded}
          onImageError={handleImageError}
        />
        <ZoomControls
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onResetZoom={handleResetZoom}
          onToggleMinimap={toggleMinimap}
          isMinimapVisible={isMinimapEnabled}
          onTogglePhotoCounts={photoMode ? togglePhotoCounts : undefined}
          arePhotoCountsVisible={showPhotoCounts}
          disabled={
            !documentId || !displayImageUrls || displayImageUrls.length === 0
          }
          zoomOutDisabled={isAtMinZoom}
          zoomInDisabled={isAtMaxZoom}
        />
        {showMinimap && (
          <LiveDocumentMinimap
            imageUrls={minimapImageUrls}
            transformStore={transformStore}
            imageNaturalDimensions={imageNaturalDimensions}
            wrapperDimensions={wrapperDimensions}
            onNavigate={handleMinimapNavigate}
            isMultipage={displayImageUrls.length > 1}
            wheelTargetRef={transformWrapperRef}
            wheelMode='dispatch'
          />
        )}
        {(isLoadingLeftPage || isLoadingRightPage) && (
          <PageLoadingIndicator>
            <PageLoadingSpinner size={16} />
          </PageLoadingIndicator>
        )}
        {leftFailure.failed && (
          <PageImageStatus pageNumber={pageNumber} retry={leftFailure.retry} />
        )}
        {pagesToPreload.map((page) => (
          <RolodexPagePreloader key={page} page={page} />
        ))}
      </DocumentViewerRoot>
    )
  }

  if (leftFailure.failed) {
    return (
      <DocumentViewerRoot ref={viewerRootRef}>
        <PageImageStatus pageNumber={pageNumber} retry={leftFailure.retry} />
      </DocumentViewerRoot>
    )
  }

  return <LoadingContainer>{t`Loading document...`}</LoadingContainer>
})
